import { useEffect, useMemo, useState } from "react"

import css from 'css'
import { Input, DefaultPopover, Button } from "twyne-new.ui"

import { CodeEditor } from "@/components/CodeEditor"

import { useCustomKeyframe } from "../../utils/tailwindConfig/hooks"

import { ANIMATION_KEYS } from "./animationsPredefined"
import { useAnimation } from "./context"

const AddPopover = ({ editing, onSubmit, trigger, keyframe: keyframeProps = {} }) => {
  const [open, setOpen] = useState(false)
  const [edit, setEdit] = useState(editing)
  const [animationName, setAnimationName] = useState(keyframeProps.name)
  const [keyframe, setKeyframe] = useState(keyframeProps.keyframe)
  const [invalidKeyframe, setInvalidKeyframe] = useState(false)

  const { animation, animation: { duplicated }, setAnimation } = useAnimation()
  const { value: keyframes } = useCustomKeyframe()

  useEffect(() => {
    if (duplicated && !edit) {
      setAnimation({ ...animation, duplicated: undefined })
      setAnimationName(duplicated.name)
      setKeyframe(duplicated.keyframe)
      setEdit(true)
      setOpen(true)
    }
  }, [duplicated, setAnimation, setAnimationName, setKeyframe, animation, keyframeProps, edit])

  useEffect(() => {
    if (keyframe === undefined) {
      setKeyframe(ANIMATION_EXAMPLE)
    }
  }, [keyframe])

  const handleChangeKeyframe = value => {
    setKeyframe(value)
    setInvalidKeyframe(false)
  }

  const handleChangeName = e => {
    setAnimationName(e.target.value.replace(/[ _]/g, ''))
    setInvalidKeyframe(false)
  }

  const handleCancel = () => {
    setOpen(false)
    setAnimationName('')
    setKeyframe(undefined)
  }

  const handleSubmit = () => {
    if (!isValidKeyframe({ name: animationName, keyframe })) {
      return setInvalidKeyframe(true)
    }

    setEdit(false)
    setOpen(false)
    setAnimationName(undefined)
    setKeyframe(undefined)
    onSubmit({ keyframe, name: animationName })
  }

  const nameAlreadyExists = useMemo(() => {
    const allKeyframes = { ...keyframes, ...ANIMATION_KEYS }
    return !edit && animationName && allKeyframes[animationName] && !edit
  }, [animationName, keyframes, edit])

  return <>
    <DefaultPopover
      trigger={trigger}
      triggerProps={{ asChild: true }}
      wrapperProps={{ open, onOpenChange: setOpen }}
      contentProps={{ className: 'w-[500px]', side: 'right', align: 'center' }}
    >
      <div className="flex flex-col gap-2">
        <div className='flex justify-between items-center'>
          <span>Name</span>
          <Input
            className='w-[80%]'
            placeholder='Keyframe name'
            value={animationName}
            onChange={handleChangeName}
          />
        </div>
        <ErrorMessage message='This keyframe name already exists' show={nameAlreadyExists} />

        <span>Keyframe</span>
        <CodeEditor value={keyframe} width='100%' height='300px' language='html' onChange={handleChangeKeyframe} />
        <ErrorMessage message='Invalid keyframe' show={invalidKeyframe} />

        <div className='flex w-full gap-2 mt-2'>
          <Button disabled={!animationName || !keyframe || nameAlreadyExists || invalidKeyframe} block color='accent' onClick={handleSubmit}>
            {edit ? 'Update' : 'Create'}
          </Button>
          <Button color='neutral' variant='outline' block onClick={handleCancel}>Cancel</Button>
        </div>
      </div>
    </DefaultPopover>
  </>
}

const ErrorMessage = ({ message, show }) => <span className={`twy-text-danger ${show ? 'block' : 'hidden'}`}>{message}</span>

function isValidKeyframe({ name, keyframe }) {
  try {
    const value = `@keyframes ${name} { ${keyframe} }`
    const parsed = css.parse(value)
    const hasValidKeyframes = parsed.stylesheet.rules.some(rule => {
      if (rule.type === 'keyframes') {
        return rule.keyframes.every(keyframe => keyframe.declarations.every(decl => decl.property && decl.value && decl.value.trim() !== ''))
      }
      return false
    })
    return !!hasValidKeyframes
  } catch (error) {
    return false
  }
}

const ANIMATION_EXAMPLE = `
<!--
Example animation keyframes
Only css, without the name:
  from { opacity: 0; }
  to { opacity: 1; }
-->`

export default AddPopover

